import React, { useState, useEffect } from 'react'

function InstanceManager({ initialInstances = '[]' }) {
  const [instances, setInstances] = useState([])
  const [newInstanceIndex, setNewInstanceIndex] = useState(0)

  useEffect(() => {
    // Parse initial instances if it's a string
    let parsedInstances = initialInstances
    if (typeof initialInstances === 'string') {
      try {
        parsedInstances = JSON.parse(initialInstances)
      } catch (e) {
        console.error('Failed to parse initialInstances:', e)
        parsedInstances = []
      }
    }
    
    // Process initial instances to add a unique key for React
    const processedInstances = (parsedInstances || []).map((instance, index) => ({
      ...instance,
      key: instance.id || `existing-${index}`,
      isExisting: true,
      isMarkedForDestruction: false
    }))
    setInstances(processedInstances)
    setNewInstanceIndex(processedInstances.length)
  }, [])

  const addInstance = () => {
    const now = new Date()
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16)

    const newInstance = {
      key: `new-${newInstanceIndex}`,
      index: newInstanceIndex,
      start: localDateTime,
      isExisting: false,
      isMarkedForDestruction: false
    }

    setInstances([...instances, newInstance])
    setNewInstanceIndex(newInstanceIndex + 1)
  }

  const removeInstance = (key) => {
    setInstances(instances.map(instance => {
      if (instance.key === key) {
        if (instance.isExisting) {
          // Mark existing instances for destruction
          return { ...instance, isMarkedForDestruction: true }
        } else {
          // Remove new instances entirely
          return null
        }
      }
      return instance
    }).filter(Boolean))
  }

  const updateInstanceDate = (key, newDate) => {
    setInstances(instances.map(instance => 
      instance.key === key ? { ...instance, start: newDate } : instance
    ))
  }

  return (
    <div>
      <div id="instances-container" className="space-y-2">
        {instances.map((instance) => {
          const isVisible = !instance.isMarkedForDestruction
          const fieldPrefix = instance.isExisting 
            ? `event[instances_attributes][${instance.index}]`
            : `event[instances_attributes][${instance.index}]`

          return (
            <div
              key={instance.key}
              className={`instance-fields flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-md ${
                isVisible ? '' : 'hidden'
              }`}
            >
              {/* Hidden fields for existing instances */}
              {instance.isExisting && (
                <>
                  <input
                    type="hidden"
                    name={`${fieldPrefix}[id]`}
                    value={instance.id}
                  />
                  <input
                    type="checkbox"
                    name={`${fieldPrefix}[_destroy]`}
                    value="1"
                    checked={instance.isMarkedForDestruction}
                    className="destroy-checkbox hidden"
                    readOnly
                  />
                </>
              )}

              <div className="flex-1">
                <label 
                  htmlFor={`event_instances_attributes_${instance.index}_start`}
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Date & Time
                </label>
                <input
                  type="datetime-local"
                  name={`${fieldPrefix}[start]`}
                  id={`event_instances_attributes_${instance.index}_start`}
                  value={instance.start}
                  onChange={(e) => updateInstanceDate(instance.key, e.target.value)}
                  className="block w-full px-3 py-2 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => removeInstance(instance.key)}
                  className="remove-instance text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  title="Remove this performance"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          )
        })}
      </div>

      <button
        id="add-instance"
        type="button"
        onClick={addInstance}
        className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <svg className="mr-2 -ml-0.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add Performance Date
      </button>
    </div>
  )
}

export default InstanceManager